import { forwardRef } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from '@phosphor-icons/react';

import { cn } from '../../utils/cn';

export const BottomSheet = DialogPrimitive.Root;
export const BottomSheetTrigger = DialogPrimitive.Trigger;
export const BottomSheetClose = DialogPrimitive.Close;

/** `scrim` do master — $scrim (45% light / 60% dark), sem blur. */
const BottomSheetOverlay = forwardRef(function BottomSheetOverlay(
  { className, ...props },
  ref,
) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      data-ds-id="ds/overlay/bottom-sheet"
      className={cn(
        'fixed inset-0 z-50 bg-scrim/45 animate-fade-in dark:bg-scrim/60',
        className,
      )}
      {...props}
    />
  );
});

/**
 * `ds/overlay/bottom-sheet--mobile` — folha que sobe de baixo: superfície de
 * card, cantos superiores $radius-ds-xl, borda $glass-border e sombra
 * $glass-shadow (0,-8, blur 32). Estrutura: grabber ($space-3 acima, 36×4) ·
 * head (título centrado $type-display-stat, padding [16,20,12,20]) · body
 * (gap $space-3, padding [8,20,20,20]).
 *
 * Com `layout="responsive"` (default) comporta-se como diálogo centrado em
 * md+ — esse papel é `ds/wizard/container--dialog` no .pen.
 */
export const BottomSheetContent = forwardRef(function BottomSheetContent(
  {
    className,
    children,
    title,
    description,
    footer,
    headerStart,
    autoFocusOnOpen = false,
    fullscreenMobile = false,
    layout = 'responsive',
    headerAlign = 'center',
    titleClassName,
    onOpenAutoFocus,
    ...props
  },
  ref,
) {
  const isBottomLayout = layout === 'bottom';
  const isStartHeader = headerAlign === 'start';

  const handleOpenAutoFocus = (event) => {
    onOpenAutoFocus?.(event);
    if (event.defaultPrevented) return;
    if (!autoFocusOnOpen) {
      event.preventDefault();
    }
  };

  const positionClasses = fullscreenMobile
    ? isBottomLayout
      ? 'inset-0 h-[100dvh] max-h-[100dvh] w-full max-w-none rounded-none animate-fade-in md:inset-x-0 md:bottom-0 md:top-auto md:left-auto md:h-auto md:max-h-[min(92dvh,100dvh)] md:w-full md:max-w-lg md:translate-x-0 md:translate-y-0 md:rounded-t-ds-xl md:animate-sheet-in'
      : 'inset-0 h-[100dvh] max-h-[100dvh] w-full max-w-none rounded-none animate-fade-in md:inset-x-auto md:left-1/2 md:top-1/2 md:bottom-auto md:h-auto md:max-h-[min(90dvh,720px)] md:w-[calc(100%-2rem)] md:max-w-lg md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-ds-xl md:animate-sheet-in'
    : isBottomLayout
      ? 'inset-x-0 bottom-0 mx-auto w-full max-w-lg max-h-[min(92dvh,100dvh)] rounded-t-ds-xl animate-sheet-in'
      : 'inset-x-0 bottom-0 mx-auto w-full max-w-lg max-h-[min(92dvh,100dvh)] rounded-t-ds-xl animate-sheet-in md:inset-x-auto md:left-1/2 md:top-1/2 md:bottom-auto md:w-[calc(100%-2rem)] md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-ds-xl md:max-h-[min(90dvh,720px)]';

  const closeButtonClasses = cn(
    'flex shrink-0 items-center justify-center rounded-ds-full bg-muted text-muted-foreground transition-colors hover:text-foreground',
    'h-8 w-8',
    !isStartHeader && 'absolute right-space-4',
    !isStartHeader &&
      (fullscreenMobile
        ? 'top-[max(1rem,env(safe-area-inset-top))] md:top-space-4'
        : 'top-space-4'),
  );

  return (
    <DialogPrimitive.Portal>
      <BottomSheetOverlay />
      <DialogPrimitive.Content
        ref={ref}
        onOpenAutoFocus={handleOpenAutoFocus}
        data-ds-id={
          isBottomLayout
            ? 'ds/overlay/bottom-sheet'
            : 'ds/wizard/container--dialog'
        }
        className={cn(
          'fixed z-50 flex flex-col overflow-hidden border border-glass-border bg-card focus:outline-none',
          // eslint-disable-next-line no-restricted-syntax -- sombra do .pen (offset/blur) com token de cor
          'shadow-[0_-8px_32px] shadow-glass-shadow',
          positionClasses,
          className,
        )}
        {...props}
      >
        {/* grabber — 36×4 em $border, $space-3 acima */}
        {!fullscreenMobile ? (
          <div className="flex shrink-0 justify-center pt-space-3" aria-hidden>
            <div className="h-1 w-9 rounded-ds-full bg-border" />
          </div>
        ) : null}

        {(title || description || headerStart) && (
          <header
            className={cn(
              'relative flex shrink-0 flex-col gap-space-1 px-space-5 pb-space-3',
              fullscreenMobile
                ? 'pt-[max(0.75rem,env(safe-area-inset-top))]'
                : 'pt-space-4',
              isStartHeader && 'flex-row items-center justify-between gap-space-3',
            )}
          >
            {isStartHeader ? (
              <>
                <div className="min-w-0 flex-1">
                  {headerStart ?? null}
                  {title ? (
                    <DialogPrimitive.Title
                      className={
                        titleClassName ||
                        'font-display text-type-display-stat font-semibold leading-tight tracking-tight text-foreground'
                      }
                    >
                      {title}
                    </DialogPrimitive.Title>
                  ) : null}
                  {description ? (
                    <DialogPrimitive.Description className="mt-space-1 text-type-body-sm leading-snug text-muted-foreground">
                      {description}
                    </DialogPrimitive.Description>
                  ) : null}
                </div>
                <DialogPrimitive.Close className={closeButtonClasses} aria-label="Fechar">
                  <X className="h-4 w-4" aria-hidden />
                </DialogPrimitive.Close>
              </>
            ) : (
              <>
                {headerStart ? (
                  <div className="flex justify-start">{headerStart}</div>
                ) : null}
                {title ? (
                  <DialogPrimitive.Title className="px-space-8 text-center font-display text-type-display-stat font-semibold leading-tight tracking-tight text-foreground">
                    {title}
                  </DialogPrimitive.Title>
                ) : null}
                {description ? (
                  <DialogPrimitive.Description className="px-space-8 text-center text-type-body-sm leading-snug text-muted-foreground">
                    {description}
                  </DialogPrimitive.Description>
                ) : null}
              </>
            )}
          </header>
        )}

        {/* body — gap $space-3, padding [$space-2, $space-5, $space-5, $space-5] */}
        <div
          className={cn(
            'min-h-0 flex-1 overflow-y-auto overscroll-contain px-space-5 pt-space-2',
            footer ? 'pb-space-2' : 'pb-space-5',
          )}
        >
          {children}
        </div>

        {footer ? (
          <footer className="shrink-0 border-t border-border bg-card px-space-5 py-space-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            {footer}
          </footer>
        ) : null}

        {!isStartHeader ? (
          <DialogPrimitive.Close className={closeButtonClasses} aria-label="Fechar">
            <X className="h-4 w-4" aria-hidden />
          </DialogPrimitive.Close>
        ) : null}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
});
